// Two roots for one domain is historical. Event/group data is cached under
// both, so a mutation that changes it must reach both — in the hook or at the
// call site. TODO(PLT-4357): consolidate under a single ['schedule'] root.

interface DateRange {
  from?: string;
  until?: string;
}

export interface DetailOptions {
  includeChildren?: boolean;
}

export interface SerializedScheduleKeyParams {
  userId: string | null;
  scheduleEventGroupIds: string;
  eventType: string;
  pastDays: number;
  pastPageSize: number | null;
  futurePageSize: number | null;
}

// Event detail/resources/reports (both platforms) + the web educational list.
export const scheduleEventKeys = {
  all: ['schedule-events'] as const,
  list: () => [...scheduleEventKeys.all, 'list'] as const,
  listPast: () => [...scheduleEventKeys.list(), 'past'] as const,
  listFuture: () => [...scheduleEventKeys.list(), 'future'] as const,
  // The plain key stays a prefix, so invalidating by detail(id) reaches both variants.
  detail: (id: string | null | undefined, { includeChildren = false }: DetailOptions = {}) =>
    includeChildren
      ? ([...scheduleEventKeys.all, 'detail', id, 'children'] as const)
      : ([...scheduleEventKeys.all, 'detail', id] as const),
  reports: () => [...scheduleEventKeys.all, 'reports'] as const,
  reportByPerson: (filters: DateRange) =>
    [...scheduleEventKeys.all, 'reports', 'by-person', filters] as const,
  reportPerson: (userId: string | null | undefined, filters: DateRange) =>
    [...scheduleEventKeys.all, 'reports', 'person', userId, filters] as const,
  reportByEvent: (filters: DateRange) =>
    [...scheduleEventKeys.all, 'reports', 'by-event', filters] as const,
};

// Schedule-data domain ('schedule'): mobile lists (useScheduleData), filters, groups.
export const scheduleKeys = {
  all: ['schedule'] as const,
  events: (params: SerializedScheduleKeyParams) => [...scheduleKeys.all, 'events', params] as const,
  pastEvents: (params: SerializedScheduleKeyParams) =>
    [...scheduleKeys.all, 'events', 'past', params] as const,
  futureEvents: (params: SerializedScheduleKeyParams) =>
    [...scheduleKeys.all, 'events', 'future', params] as const,
  filters: () => [...scheduleKeys.all, 'filters'] as const,
  groups: (type: string | null | undefined) => [...scheduleKeys.all, 'groups', type] as const,
};

interface PagedEvents {
  events?: unknown[];
  total?: number;
}

// Next page number for the paged schedule-events endpoints, or undefined when
// all pages are loaded. A missing/non-numeric `total` ends pagination — safer
// than paging forever against a broken response contract.
export const nextEventsPage = (
  lastPage: PagedEvents,
  allPages: PagedEvents[],
): number | undefined => {
  const loaded = allPages.reduce((count, page) => count + (page.events?.length ?? 0), 0);
  return typeof lastPage.total === 'number' && loaded < lastPage.total
    ? allPages.length + 1
    : undefined;
};
